/*
 * theme.ts
 * -----------------------------------------------------------------------------
 * Which colour scheme the page is in, and how to be told when it changes.
 *
 * Two hosts change theme by different means, and the canvases have to follow
 * both:
 *
 *   - The desktop WebView is pointed at (or away from) the OS scheme by the
 *     native shell (WebBridge.ApplyTheme), so prefers-color-scheme is the whole
 *     story there. That is all this module used to check, inline in main.ts and
 *     Canvas2DRenderer.ts.
 *
 *   - A browser cannot be told what the OS prefers. Settings ▸ UI Theme stamps
 *     data-theme="light"/"dark" on <html> instead (BridgeHost.setTheme in
 *     index.html) and removes it for "System" — so in the web viewer the media
 *     query never moved and the 3D/2D views stayed light while the shell around
 *     them went dark.
 *
 * Hence: an explicit data-theme wins, the media query is the fallback, and
 * watchTheme() listens for both. The attribute is watched with a
 * MutationObserver rather than a callback from the host, so this works no
 * matter who writes it (Blazor interop, the Open Model page's own toggle, or
 * devtools) and needs no cooperation from either index.html.
 */

/** True when the page should render dark right now. */
export function prefersDark(): boolean {
  const explicit = document.documentElement.getAttribute('data-theme');
  if (explicit === 'dark') return true;
  if (explicit === 'light') return false;
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/**
 * Call `onChange` whenever the effective scheme may have changed. Returns a
 * disposer that unhooks both listeners — call it from the owner's dispose(),
 * or the observer keeps a reference to a dead renderer alive.
 */
export function watchTheme(onChange: () => void): () => void {
  const mql =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;
  mql?.addEventListener('change', onChange);

  // Writing an attribute fires no event of its own.
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });

  return () => {
    mql?.removeEventListener('change', onChange);
    observer.disconnect();
  };
}
